import { useEffect, useMemo, useState } from "react"
import { NO_CATALOG, RUSSIAN_LANGUAGE, VIA_RU_NOTE } from "../api/config"
import { describeError } from "../api/http"
import {
	useCatalogs,
	useLanguages,
	useLatvianWebsites,
	useTranslates,
	useTranslateUrls,
} from "../api/queries"
import { TranslateSource, type TranslateRow } from "../api/types"
import { useAccess } from "../auth/rights"
import { useAuth } from "../auth/AuthContext"
import { Note } from "../components/Note"
import Pager from "../components/Pager"
import { useConfirm } from "../hooks/useConfirm"
import { usePaged } from "../hooks/usePaged"
import { useStoredFlag, useStoredNumber } from "../hooks/useStored"
import { confirmDiscard, useUnsaved } from "../hooks/useUnsaved"
import { useLanguageId } from "../lib/language"
import { StorageKey } from "../lib/storage"
import CatalogSelect from "./dictionary/CatalogSelect"
import DictionaryRow, { TranslateIcon } from "./dictionary/DictionaryRow"
import {
	describeUrlsError,
	escapeCsv,
	LINKABLE,
	sameTextKey,
	SOURCE_NAME,
	SOURCES,
	splitCatalogs,
	symbols,
} from "./dictionary/rules"
import SyncPanel from "./dictionary/SyncPanel"
import { useEdits } from "./dictionary/useEdits"

/**
 * Переводы — единственный экран перевода в рижском интерфейсе.
 *
 * Он собран из двух страниц старого фронта: справочника (`app/bullfacts/`) и
 * формы переводчика (`app/translator-page/`). Форма отличалась от справочника
 * тремя вещами — ссылка на товар на сайте, счётчик символов и выгрузка, — и
 * ради них держала второй набор фильтров, второй запрос и второй способ
 * сохранения. Здесь это три переключателя над той же таблицей.
 *
 * Отличия от старого фронта, все намеренные:
 *
 * 1. Набор тянется один раз на пару «каталог + раздел» и живёт в кэше. Поиск,
 *    отбор пустых и счётчики считаются на клиенте. В старом фронте каждая буква
 *    поиска и каждое переключение фильтра уходили на сервер, а запрос там
 *    тяжёлый — до трёх секунд.
 * 2. Правки копятся и уходят одной пачкой (`save-translated-values`) вместо
 *    отдельного запроса на каждый Enter. Несохранённое видно и посчитано.
 * 3. Видно разнос по одинаковым фразам: бэк пишет перевод во все строки раздела
 *    с тем же русским текстом, и это единственное место, где об этом сказано.
 * 4. Разные переводы одной и той же русской фразы в одной пачке не пропускаются:
 *    бэк в этом случае молча берёт первый и ставит его во все.
 * 5. Машинный перевод — на выделенные строки или на все пустые в текущем
 *    отборе. Идёт пачками: виден прогресс, его можно прервать, и уже
 *    переведённое не пропадает. Результат ложится в поля черновиком и уходит
 *    в базу только вместе со всем остальным.
 * 6. Сохранение тоже пачками по 200 строк — столько же берёт партиция внутри
 *    `TranslateEditor`. Если оборвалось на середине, сохранённое остаётся
 *    сохранённым, а в полях остаётся только то, что не доехало.
 * 7. Символы считаются на клиенте, тем же правилом, что и на бэке: без пробелов
 *    (`TranslateReader.cs:117`). Отдельный запрос ради счётчика не нужен.
 * 8. Ссылки на товар на сайте — по требованию: `with-urls` гоняет ту же тяжёлую
 *    выборку целиком, поэтому запрос уходит, только когда колонку включили.
 * 9. Выгрузка — CSV того, что на экране, вместе с несохранёнными черновиками.
 *    Серверный `\api\file\translates-info` не берём: он перечитывает выборку
 *    своими параметрами, не знает ни про раздел, ни про черновики.
 * 10. Разделы названы по-русски, а не именами таблиц (`AttrNames`, `CatKat`).
 * 11. Поиск по смыслу (`FREETEXT` по всей таблице `Translates`) убран: он искал
 *    в наборе, который и так весь на клиенте, а обычный поиск подстроки не
 *    выкидывает из запроса знаки препинания, как это делает `FixSearchString`.
 *
 * Правка черновиков, сохранение и машинный перевод живут в `dictionary/useEdits`,
 * правила разноса и подсчёта символов — в `dictionary/rules`.
 */

const PAGE_SIZE = 50

type Status = "all" | "empty" | "filled"

export default function Dictionary() {
	const languageId = useLanguageId()
	const { session } = useAuth()
	const { isManager, isTranslator } = useAccess()
	const { ask, dialog } = useConfirm()

	const catalogs = useCatalogs()
	const websites = useLatvianWebsites()
	const languages = useLanguages()

	// null — каталог ещё не выбран: подставим первый сайт Риги, когда придут списки
	const [catalogId, setCatalogId] = useStoredNumber(
		StorageKey.dictionaryCatalog,
		null
	)
	const [source, setSource] = useStoredNumber(
		StorageKey.dictionarySource,
		TranslateSource.All
	)
	const [websiteId, setWebsiteId] = useStoredNumber(
		StorageKey.dictionarySite,
		null
	)

	// колонки формы переводчика: счётчик символов и ссылка на товар на сайте
	const [showSymbols, setShowSymbols] = useStoredFlag(
		StorageKey.dictionarySymbols
	)
	const [showLinks, setShowLinks] = useStoredFlag(StorageKey.dictionaryLinks)

	const [status, setStatus] = useState<Status>("all")
	const [search, setSearch] = useState("")
	const [selected, setSelected] = useState<Set<number>>(new Set())
	const [syncOpen, setSyncOpen] = useState(false)
	const [hintsOpen, setHintsOpen] = useState(false)

	const isRussian = languageId === RUSSIAN_LANGUAGE
	// save-translated-values, машинный перевод и with-urls открыты только Lv и Rg;
	// у Translator есть построчный PUT /api/translate (TranslateController.cs:125,143)
	const canEdit = !isRussian && (isManager || isTranslator)
	const language = (languages.data ?? []).find(item => item.id === languageId)
	const languageName = language?.name ?? `язык ${languageId}`
	const canMachine = isManager && !isRussian && !!language?.deeplLanguageCode

	const sites = useMemo(() => websites.data ?? [], [websites.data])
	const catalogName = (catalogs.data ?? []).find(
		item => item.id === catalogId
	)?.name

	// Первый вход: встаём на первый каталог сайта Риги — основная их работа там.
	// Сюда же попадает случай, когда запомненный каталог из фильтра убрали.
	useEffect(() => {
		if (!catalogs.data || !websites.data) return
		const { riga, printed } = splitCatalogs(
			catalogs.data,
			new Set(websites.data.map(site => site.name))
		)
		const allowed = new Set([
			NO_CATALOG,
			...riga.map(catalog => catalog.id),
			...printed.map(catalog => catalog.id),
		])
		if (catalogId !== null && allowed.has(catalogId)) return
		setCatalogId(riga[0]?.id ?? NO_CATALOG)
	}, [catalogId, catalogs.data, websites.data, setCatalogId])

	// Сайт для ссылок: по умолчанию тот, чьё имя совпадает с выбранным каталогом
	// (у сайтов Риги имена каталога и сайта одинаковые), иначе первый в списке.
	useEffect(() => {
		if (sites.length === 0) return
		if (websiteId !== null && sites.some(site => site.id === websiteId)) return
		setWebsiteId((sites.find(site => site.name === catalogName) ?? sites[0]).id)
	}, [sites, catalogName, websiteId, setWebsiteId])

	// Вне каталогов бэк отдаёт одну выборку и раздел не смотрит
	// (`TranslateReader.cs:156-157`) — не плодим одинаковые наборы в кэше.
	const effectiveSource: TranslateSource =
		catalogId === NO_CATALOG ? TranslateSource.All : source

	const translates = useTranslates(catalogId, effectiveSource, languageId)
	const rows = useMemo(() => translates.data ?? [], [translates.data])

	const edits = useEdits({
		rows,
		catalogId,
		source: effectiveSource,
		languageId,
		languageName,
		deeplCode: language?.deeplLanguageCode ?? "",
		isManager,
		translator: session?.username ?? "riga",
		ask,
	})

	// шапка спросит перед сменой языка — она пересоздаёт экран
	useUnsaved(edits.dirtyCount)

	// Ссылки живут в отдельной выборке (`with-urls`) и стоят столько же, сколько
	// весь набор, — тянем их, только когда колонку включили.
	//
	// Сейчас колонка выключена совсем: `with-urls` — единственный метод
	// справочника, который лезет в MySQL самого сайта, и делает это из России
	// (`UrlGroupProvider.GetUrlAliasesAsync`). Оттуда база сайта не отвечает, а
	// зависший запрос роняет процесс `ecm`. Вернём, когда ссылки будет отдавать
	// рижский узел (`PLAN.md`, фаза 7-Л).
	const canLink = false
	const linksOn = showLinks && canLink
	const urls = useTranslateUrls(catalogId, websiteId, languageId, linksOn)
	const urlOf = (row: TranslateRow) =>
		LINKABLE.has(row.source) ? (urls.data?.get(row.sourceId) ?? null) : null

	const filtered = useMemo(() => {
		const needle = search.trim().toLowerCase()
		return rows.filter(row => {
			const stored = row.translate ?? ""
			if (status === "empty" && stored !== "") return false
			if (status === "filled" && stored === "") return false
			if (!needle) return true
			return (
				(row.russian ?? "").toLowerCase().includes(needle) ||
				stored.toLowerCase().includes(needle) ||
				String(row.translateId).includes(needle)
			)
		})
	}, [rows, status, search])

	const paged = usePaged(filtered, PAGE_SIZE)

	const counts = useMemo(() => {
		let empty = 0
		for (const row of rows) if ((row.translate ?? "") === "") empty++
		return { all: rows.length, empty, filled: rows.length - empty }
	}, [rows])

	/** Пустые строки текущего отбора — то, ради чего запускают перевод пачкой.
	 *  Уже переведённое машина не трогает: правку руками она бы затёрла. */
	const emptyInView = useMemo(
		() =>
			filtered.filter(
				row =>
					(row.translate ?? "") === "" &&
					edits.edits[row.id] === undefined &&
					!!row.russian?.trim()
			),
		[filtered, edits.edits]
	)

	/** Символы отбора — по ним переводчикам считают работу. */
	const charsInView = useMemo(() => {
		const seen = new Set<string>()
		let total = 0
		for (const row of filtered) {
			const key = sameTextKey(row)
			if (key && seen.has(key)) continue
			if (key) seen.add(key)
			total += symbols(row.russian)
		}
		return total
	}, [filtered])

	/** Непереведённые описания в наборе — их пункты выпадут при «Применить». */
	const untranslatedDescriptions = useMemo(
		() =>
			rows.filter(
				row =>
					row.source === TranslateSource.PictGroupDesc &&
					(row.translate ?? "") === ""
			).length,
		[rows]
	)

	// Смена каталога, раздела или языка меняет набор — правки к нему больше
	// не относятся. Спрашиваем перед уходом, чтобы не потерять их молча.
	const switchSet = (next: () => void) => {
		if (!confirmDiscard("Сменить набор и потерять их?")) return
		edits.discardAll()
		setSelected(new Set())
		paged.reset()
		next()
	}

	/** Выгрузка того, что на экране: текущий отбор со всеми колонками и
	 *  несохранёнными черновиками. CSV с `sep=;` и BOM — Excel открывает его
	 *  двойным кликом и не ломает кириллицу. */
	const exportCsv = () => {
		const head = [
			"Код",
			"Раздел",
			"Русский",
			languageName,
			"Статус",
			"Символов",
			"Ссылка",
		]
		const lines = filtered.map(row => {
			const url = urlOf(row)
			return [
				String(row.translateId),
				SOURCE_NAME.get(row.source) ?? "",
				row.russian ?? "",
				edits.valueOf(row),
				edits.isDirty(row)
					? "черновик"
					: row.translate
						? "сохранено"
						: "пусто",
				String(symbols(row.russian)),
				url ? `https://${url}` : "",
			]
		})
		// BOM — иначе Excel читает кириллицу в системной кодировке;
		// `sep=;` — иначе он же делит строку по запятой
		const csv =
			"﻿sep=;\r\n" +
			[head, ...lines].map(cells => cells.map(escapeCsv).join(";")).join("\r\n")

		const stamp = new Date().toISOString().slice(0, 10)
		const name = `perevody-${catalogName ?? "vne-katalogov"}-${languageName}-${stamp}.csv`.replace(
			/[\\/:*?"<>|]+/g,
			"-"
		)

		const link = document.createElement("a")
		const href = URL.createObjectURL(
			new Blob([csv], { type: "text/csv;charset=utf-8" })
		)
		link.href = href
		link.download = name
		link.click()
		URL.revokeObjectURL(href)
		edits.setNotice(`Выгружено строк: ${filtered.length}`)
	}

	// Ctrl+S — как в табличной части карточки
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			// code, а не key: на русской раскладке key даёт «ы»
			if (!(event.ctrlKey || event.metaKey) || event.code !== "KeyS") return
			event.preventDefault()
			void edits.save()
		}
		window.addEventListener("keydown", onKeyDown)
		return () => window.removeEventListener("keydown", onKeyDown)
	})

	// уход со страницы с несохранённым
	useEffect(() => {
		if (edits.dirtyCount === 0) return
		const onLeave = (event: BeforeUnloadEvent) => event.preventDefault()
		window.addEventListener("beforeunload", onLeave)
		return () => window.removeEventListener("beforeunload", onLeave)
	}, [edits.dirtyCount])

	const pageIds = paged.rows.map(row => row.id)
	const allOnPageSelected =
		pageIds.length > 0 && pageIds.every(id => selected.has(id))

	// пока каталог не выбран, запрос выключен — но экран всё равно занят делом
	const loading = catalogId === null || translates.isLoading

	const percent =
		counts.all === 0 ? 100 : Math.round((counts.filled / counts.all) * 100)

	const columnCount =
		5 +
		(effectiveSource === TranslateSource.AttrNames ? 2 : 0) +
		(showSymbols ? 1 : 0) +
		(linksOn ? 1 : 0)

	return (
		<>
			{edits.error && (
				<Note tone="error" onDismiss={() => edits.setError(null)}>
					{edits.error}
				</Note>
			)}
			{edits.notice && (
				<Note tone="info" onDismiss={() => edits.setNotice(null)}>
					{edits.notice}
				</Note>
			)}
			{isRussian && (
				<Note tone="hint">
					Выбран русский язык — это исходный текст, а не перевод. Правится он
					в российском интерфейсе. Выберите в шапке язык перевода.
				</Note>
			)}

			<div className="toolbar dict-toolbar">
				<label className="dict-field">
					<span>Каталог</span>
					<CatalogSelect
						catalogs={catalogs.data ?? []}
						rigaSites={new Set(sites.map(site => site.name))}
						value={catalogId ?? NO_CATALOG}
						onChange={next =>
							switchSet(() => {
								setCatalogId(next)
								// у сайтов Риги имя каталога и имя сайта совпадают —
								// ссылки должны вести на тот же сайт, что выбран слева
								const name = (catalogs.data ?? []).find(
									item => item.id === next
								)?.name
								const site = sites.find(item => item.name === name)
								if (site) setWebsiteId(site.id)
							})
						}
					/>
				</label>

				<label className="dict-field">
					<span>Раздел</span>
					<select
						className="select"
						value={source}
						// вне каталогов бэк отдаёт единственную выборку и раздел игнорирует
						disabled={catalogId === NO_CATALOG}
						onChange={event => {
							const next = Number(event.target.value)
							switchSet(() => setSource(next))
						}}
					>
						{SOURCES.map(item => (
							<option key={item.value} value={item.value}>
								{item.label}
							</option>
						))}
					</select>
				</label>

				<div className="search">
					<span className="search__icon">⌕</span>
					<input
						className="input"
						placeholder="Поиск по русскому, переводу или коду"
						value={search}
						onChange={event => {
							setSearch(event.target.value)
							paged.reset()
						}}
					/>
				</div>

				<button
					className="btn"
					disabled={translates.isFetching}
					title="Перечитать набор с сервера. Несохранённые правки останутся"
					onClick={() => void translates.refetch()}
				>
					{translates.isFetching ? "…" : "↻"}
				</button>
			</div>

			<div className="dict-extras">
				<label
					className="dict-check"
					title="Символы русского текста без пробелов — так их считает бэк и так их считают переводчикам"
				>
					<input
						type="checkbox"
						checked={showSymbols}
						onChange={event => setShowSymbols(event.target.checked)}
					/>
					Символы
				</label>

				<label
					className="dict-check"
					title={VIA_RU_NOTE}
				>
					<input
						type="checkbox"
						checked={linksOn}
						disabled={!canLink}
						onChange={event => setShowLinks(event.target.checked)}
					/>
					Ссылки на сайт
				</label>

				{linksOn && (
					<label className="dict-field">
						<span>Сайт</span>
						<select
							className="select"
							value={websiteId ?? 0}
							onChange={event => setWebsiteId(Number(event.target.value))}
						>
							{sites.map(site => (
								<option key={site.id} value={site.id}>
									{site.name}
								</option>
							))}
						</select>
					</label>
				)}

				{linksOn && urls.isFetching && (
					<span className="muted">
						<span className="spinner" /> ссылки тянутся
					</span>
				)}
				{linksOn && urls.isError && (
					<span className="muted">
						ссылки не пришли: {describeUrlsError(urls.error)}
					</span>
				)}

				<span className="topbar__spacer" />

				<span
					className="muted"
					title="Символы без пробелов, одинаковые фразы посчитаны один раз"
				>
					символов в отборе: {charsInView.toLocaleString("ru")}
				</span>

				<button
					className="btn"
					disabled={filtered.length === 0}
					title="Выгрузить то, что сейчас в отборе, вместе с несохранёнными черновиками"
					onClick={exportCsv}
				>
					Выгрузить CSV
				</button>

				{isManager && (
					<button
						className="btn"
						aria-expanded={syncOpen}
						title="Собрать в справочник новые фразы и разложить переводы по таблицам базы"
						onClick={() => setSyncOpen(state => !state)}
					>
						{syncOpen ? "▾" : "▸"} Пополнить и применить
					</button>
				)}

				<button
					className="btn btn--ghost"
					aria-expanded={hintsOpen}
					title="Горячие клавиши и правила справочника"
					onClick={() => setHintsOpen(state => !state)}
				>
					?
				</button>
			</div>

			{hintsOpen && (
				<Note tone="info">
					Enter — следующая строка, Shift+Enter — перенос строки, Esc — вернуть
					как было, Ctrl+S — сохранить. Перевод уходит во все строки раздела
					с тем же русским текстом — где таких несколько, у строки стоит «×N»,
					и в переводчик такая фраза уйдёт один раз. Машинный перевод ложится
					в поля черновиком: пока не нажали «Сохранить», в базе ничего нет.
				</Note>
			)}

			{isManager && syncOpen && (
				<SyncPanel
					languageId={languageId}
					languageName={languageName}
					isRussian={isRussian}
					untranslatedDescriptions={untranslatedDescriptions}
					disabled={edits.busy}
				/>
			)}

			<div className="dict-status">
				<div className="segmented">
					{(["all", "empty", "filled"] as Status[]).map(item => (
						<button
							key={item}
							aria-pressed={status === item}
							onClick={() => {
								setStatus(item)
								paged.reset()
							}}
						>
							{item === "all"
								? "Все"
								: item === "empty"
									? "Пустые"
									: "Переведены"}{" "}
							· {counts[item].toLocaleString("ru")}
						</button>
					))}
				</div>

				<div className="dict-progress" title="Доля переведённых строк набора">
					<div className="dict-progress__bar">
						<i style={{ width: `${percent}%` }} />
					</div>
					<span className="muted">{percent}%</span>
				</div>

				<div className="counter">
					{loading
						? "загрузка…"
						: `найдено ${filtered.length.toLocaleString("ru")}`}
				</div>

				<span className="topbar__spacer" />

				{selected.size > 0 && (
					<span className="dict-selected">выделено {selected.size}</span>
				)}

				{canMachine && (
					<>
						<button
							className="btn"
							disabled={edits.busy || selected.size === 0}
							title={`Перевести выделенные через переводчик на ${languageName}`}
							onClick={() =>
								void edits.machineTranslate(
									rows.filter(row => selected.has(row.id))
								)
							}
						>
							<TranslateIcon />
							Перевести выделенные
						</button>

						<button
							className="btn"
							disabled={edits.busy || emptyInView.length === 0}
							title="Перевести все пустые строки текущего отбора — каталог, раздел, поиск. Одинаковые фразы уйдут в переводчик один раз"
							onClick={() => void edits.machineTranslate(emptyInView)}
						>
							<TranslateIcon />
							Перевести пустые · {emptyInView.length.toLocaleString("ru")}
						</button>
					</>
				)}

				{edits.dirtyCount > 0 && (
					<span className="tp-dirty">не сохранено: {edits.dirtyCount}</span>
				)}

				<button
					className="btn btn--primary"
					disabled={!canEdit || edits.busy || edits.dirtyCount === 0}
					title="Сохранить все правки (Ctrl+S)"
					onClick={() => void edits.save()}
				>
					{edits.busy && edits.progress?.kind === "save"
						? "Сохранение…"
						: "Сохранить"}
				</button>
			</div>

			{edits.progress && (
				<div className="dict-run">
					<div className="dict-run__bar">
						<i
							style={{
								width: `${
									edits.progress.total === 0
										? 0
										: Math.round(
												(edits.progress.done / edits.progress.total) * 100
											)
								}%`,
							}}
						/>
					</div>
					<span>
						{edits.progress.kind === "translate"
							? "Перевод фраз"
							: "Сохранение строк"}
						: {edits.progress.done.toLocaleString("ru")} из{" "}
						{edits.progress.total.toLocaleString("ru")}
					</span>
					<button
						className="btn"
						disabled={edits.cancelled.current}
						title="Остановить после текущей пачки. Уже сделанное останется"
						onClick={edits.cancel}
					>
						Остановить
					</button>
				</div>
			)}

			{edits.conflicts.length > 0 && (
				<Note tone="warn">
					Одной русской фразе заданы разные переводы — бэк разнесёт по всем
					строкам раздела только один. Приведите к одному значению: «
					{edits.conflicts[0]}»
					{edits.conflicts.length > 1 &&
						` и ещё ${edits.conflicts.length - 1}`}
				</Note>
			)}

			<div className="table-wrap">
				{translates.isError && (
					<div className="state">
						Не удалось загрузить справочник: {describeError(translates.error)}
					</div>
				)}

				{loading && !translates.isError && (
					<div className="state">
						<span className="spinner" /> Набор тянется целиком — дальше поиск и
						фильтры работают без сервера
					</div>
				)}

				{!loading && !translates.isError && (
					<table className="products dict-table">
						<thead>
							<tr>
								<th className="dict-table__pick">
									<input
										type="checkbox"
										checked={allOnPageSelected}
										title="Выделить строки страницы"
										onChange={() =>
											setSelected(state => {
												const next = new Set(state)
												for (const id of pageIds) {
													if (allOnPageSelected) next.delete(id)
													else next.add(id)
												}
												return next
											})
										}
									/>
								</th>
								<th className="dict-table__code">Код</th>
								<th>Русский</th>
								<th className="dict-table__value">{languageName}</th>
								{effectiveSource === TranslateSource.AttrNames && (
									<>
										<th className="dict-table__extra">Короткое название</th>
										<th className="dict-table__extra">Ед. изм.</th>
									</>
								)}
								{showSymbols && (
									<th
										className="dict-table__symbols"
										title="Символы русского текста без пробелов"
									>
										Символов
									</th>
								)}
								{linksOn && <th className="dict-table__link">Ссылка</th>}
								<th className="dict-table__act" />
							</tr>
						</thead>
						<tbody>
							{paged.rows.length === 0 && (
								<tr>
									<td colSpan={columnCount} className="muted">
										Ничего не найдено
									</td>
								</tr>
							)}
							{paged.rows.map(row => {
								const key = sameTextKey(row)
								return (
									<DictionaryRow
										key={row.id}
										row={row}
										value={edits.valueOf(row)}
										dirty={edits.isDirty(row)}
										twins={(key && edits.sameText.get(key)?.length) || 1}
										showSource={effectiveSource === TranslateSource.All}
										showExtras={
											effectiveSource === TranslateSource.AttrNames
										}
										showSymbols={showSymbols}
										url={linksOn ? urlOf(row) : undefined}
										extras={edits.extras}
										editable={canEdit}
										canMachine={canMachine}
										picked={selected.has(row.id)}
										onPick={() =>
											setSelected(state => {
												const next = new Set(state)
												if (next.has(row.id)) next.delete(row.id)
												else next.add(row.id)
												return next
											})
										}
										onChange={value => edits.setValue(row, value)}
										onRevert={() => edits.revert(row)}
										onExtra={(field, value) =>
											edits.setExtra(row, field, value)
										}
										onMachine={() => void edits.machineTranslate([row])}
									/>
								)
							})}
						</tbody>
					</table>
				)}

				<Pager
					page={paged.page}
					pageCount={paged.pageCount}
					onGo={paged.goTo}
				/>
			</div>

			{dialog}
		</>
	)
}
